import React, { useState } from 'react';
import { Step, StepLabel, Stepper, Button } from '@material-ui/core';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';

import Dates from './Components/Dates';
import FinalData from './Components/FinalData';
import { generateMontlyReport } from '../../services/reportService';
import { monthsToReport, yearsToReport } from '../../utils/dates';
import { notifications } from '../../utils/messages';
import Products from './Components/Products';
import Summary from './Components/Summary';
import BasicInfo from './Components/BasicInfo';
import Loading from '../common/loading';

const getSteps = () => [
  'Seleccione la fecha *',
  'Seleccione el Proyecto e Ingrese las actividades ejecutadas *',
  'Ingrese la producción científica (opcional)',
  'Ingrese los datos finales *',
  'Resumen',
];

const Monthly = () => {
  const initialState = {
    year: '',
    month: '',
    period: '',
    type: '',
    projectSlug: '',
    projectName: '',
    executedActivities: [
      {
        name: '',
        week: [false, false, false, false],
      },
    ],
    publications: [
      {
        name: '',
        info: '',
        type: '',
      },
    ],
    theses: [
      {
        name: '',
        type: '',
        status: '',
      },
    ],
    results: '',
    conclusions: '',
    recommendations: '',
    percentage: '',
  };
  const [state, setState] = useState(initialState);

  const [months, setMonths] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();
  const [isLoading, setIsLoading] = useState(false);
  const [urlPDF, setUrlPDF] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSetYear = (selectedYear) => {
    setState((prevState) => ({
      ...prevState,
      year: selectedYear,
      month: '',
      period: '',
    }));
    const months = monthsToReport(selectedYear);
    setMonths(months);
  };

  const handleMonth = (selectedMonth) => {
    setState((prevState) => ({ ...prevState, month: selectedMonth }));
  };

  const handlePeriod = (period) => {
    setState((prevState) => ({ ...prevState, period: period.toUpperCase() }));
  };

  const handleType = (value) => {
    const data = { ...state };
    data['type'] = value;
    setState(data);
  };

  const handleProject = (project) => {
    setState((prevState) => ({
      ...prevState,
      projectSlug: project.slug,
      projectName: project.name,
    }));
  };

  const handleAddActivity = () => {
    const newExecutedAcivities = [
      ...state.executedActivities,
      { name: '', week: [false, false, false, false] },
    ];
    setState((prevState) => ({
      ...prevState,
      executedActivities: newExecutedAcivities,
    }));
  };

  const handleDeleteActivity = (index) => {
    const executedActivities = [...state.executedActivities];
    const newExecutedActivities = executedActivities.filter((act, i) =>
      index !== i ? { ...act } : null
    );
    setState((prevState) => ({
      ...prevState,
      executedActivities: newExecutedActivities,
    }));
  };

  const handleAddPublication = () => {
    const publications = [...state.publications, { name: '', type: '' }];
    setState((prevState) => ({
      ...prevState,
      publications,
    }));
  };

  const handleInputChange = (e, index, entity) => {
    const { name, value } = e.target;
    const data = { ...state };
    data[entity][index][name] = value;
    setState(data);
  };

  const handleAddThesis = () => {
    const theses = [...state.theses, { name: '', type: '', status: '' }];
    setState((prevState) => ({
      ...prevState,
      theses,
    }));
  };

  const handleDeletePublication = (index, entity) => {
    const data = { ...state };

    const filteredData = data[entity].filter((dat, i) =>
      index !== i ? { ...dat } : null
    );
    setState((prevState) => ({
      ...prevState,
      [entity]: filteredData,
    }));
  };

  const handleCheckBoxChange = (e, index) => {
    const { name, checked } = e.target;
    const newExecutedActivities = [...state.executedActivities];
    newExecutedActivities[index].week[name] = checked;
    setState((prevState) => ({
      ...prevState,
      executedActivities: newExecutedActivities,
    }));
  };

  const handleChange = ({ target: input }) => {
    const data = { ...state };
    data[input.name] = input.value;
    setState(data);
  };

  const handleGeneratePDF = async () => {
    setIsLoading(true);
    try {
      const { data } = await generateMontlyReport(state);
      setUrlPDF(data.urlReport);
      enqueueSnackbar(notifications.PDF_SUCCESS, { variant: 'success' });
      handleNext();
    } catch (ex) {
      console.log(ex.response);
      enqueueSnackbar(notifications.PDF_ERROR, { variant: 'error' });
      if (ex.response.status === 500) {
        enqueueSnackbar(ex.response.data.substring(48, 400), {
          variant: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPDF = (event) => {
    event.preventDefault();
    if (urlPDF !== null) window.open(urlPDF);
  };

  const reset = () => {
    setActiveStep(0);
    setState(initialState);
    setUrlPDF('');
  };

  const getStepContent = (stepIndex) => {
    const {
      period,
      projectSlug,
      year,
      month,
      executedActivities: activities,
      publications,
      theses,
      results,
      conclusions,
      recommendations,
      percentage,
    } = state;
    switch (stepIndex) {
      case 0:
        return (
          <Dates
            months={months}
            onMonth={handleMonth}
            onPeriod={handlePeriod}
            onSetYear={handleSetYear}
            period={period}
            selectedMonth={month}
            selectedYear={year}
            years={yearsToReport()}
          />
        );
      case 1:
        return (
          <BasicInfo
            activities={activities}
            onAddActivity={handleAddActivity}
            onChange={handleInputChange}
            onChecked={handleCheckBoxChange}
            onDelete={handleDeleteActivity}
            projectSlug={projectSlug}
            onProject={handleProject}
            year={year}
          />
        );
      case 2:
        return (
          <Products
            data1={publications}
            data2={theses}
            onAddPublication={handleAddPublication}
            onAddThesis={handleAddThesis}
            onChange={handleInputChange}
            onDelete={handleDeletePublication}
          />
        );
      case 3:
        return (
          <FinalData
            conclusions={conclusions}
            onChange={handleChange}
            percentage={percentage}
            recomendations={recommendations}
            results={results}
          />
        );
      case 4:
        return <Summary data={state} onType={handleType} url={urlPDF} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Report>
      <h1>Informe Mensual</h1>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        style={{ width: '100%' }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep === steps.length ? (
        <FooterStepper>
          <OpenPdf>
            <Loading open={isLoading} />
            <LinkItem to='#' onClick={handleOpenPDF}>
              Abrir PDF
            </LinkItem>
            <Button onClick={reset}>Finalizar</Button>
          </OpenPdf>
        </FooterStepper>
      ) : (
        <>
          {getStepContent(activeStep)}
          <FooterStepper>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Atrás
            </Button>
            {((activeStep === 0 &&
              state.year !== '' &&
              state.month !== '' &&
              state.period !== '') ||
              (activeStep === 1 &&
                state.projectSlug !== '' &&
                state.executedActivities.length > 0 &&
                state.executedActivities[0].name.length > 3 &&
                state.executedActivities[0].week.some((w) => w === true)) ||
              activeStep === 2 ||
              (activeStep === 3 && state.percentage !== '')) && (
              <Button variant='contained' color='primary' onClick={handleNext}>
                Siguiente
              </Button>
            )}
            {activeStep === 4 && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleGeneratePDF}
              >
                Generar PDF
              </Button>
            )}
          </FooterStepper>
        </>
      )}
    </Report>
  );
};

const FooterStepper = styled.div`
  margin: 3em 0 2em;
  display: flex;
  width: 100%;
  justify-content: center;
`;

const LinkItem = styled(Link)`
  width: 100%;
  text-align: center;
  margin-bottom: 2em;
`;

const OpenPdf = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const Report = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  align-content: flex-start;
  justify-content: center;
  width: 100%;
`;

export default Monthly;
